/* eslint-disable max-params */
import { create } from "zustand"

type DeletionProgress = {
  total: number
  completed: number
  failed: string[]
}

type ProjectFileSelectionStore = {
  selectedFiles: Set<string>
  deletionProgress: DeletionProgress | null
  lastSelectedIndex: number | null
  toggleFile: (filename: string, index?: number) => void
  selectRange: (
    filenames: string[],
    fromIndex: number,
    toIndex: number,
    selectableFilenames?: Set<string>
  ) => void
  selectAll: (filenames: string[]) => void
  deselectAll: () => void
  isSelected: (filename: string) => boolean
  setDeletionProgress: (progress: DeletionProgress | null) => void
  reset: () => void
}

export const useProjectFileSelectionStore = create<ProjectFileSelectionStore>((set, get) => ({
  selectedFiles: new Set(),
  deletionProgress: null,
  lastSelectedIndex: null,

  toggleFile: (filename: string, index?: number) => {
    set((state) => {
      const newSelected = new Set(state.selectedFiles)
      if (newSelected.has(filename)) {
        newSelected.delete(filename)
      } else {
        newSelected.add(filename)
      }
      return {
        selectedFiles: newSelected,
        lastSelectedIndex: index ?? state.lastSelectedIndex,
      }
    })
  },

  selectRange: (
    filenames: string[],
    fromIndex: number,
    toIndex: number,
    selectableFilenames?: Set<string>
  ) => {
    const startIndex = Math.min(fromIndex, toIndex)
    const endIndex = Math.max(fromIndex, toIndex)
    set((state) => {
      const newSelected = new Set(state.selectedFiles)
      for (let index = startIndex; index <= endIndex; index++) {
        const filename = filenames[index]
        if (filename && (!selectableFilenames || selectableFilenames.has(filename))) {
          newSelected.add(filename)
        }
      }
      return {
        selectedFiles: newSelected,
        lastSelectedIndex: toIndex,
      }
    })
  },

  selectAll: (filenames: string[]) => {
    set({ selectedFiles: new Set(filenames) })
  },

  deselectAll: () => {
    set({ selectedFiles: new Set() })
  },

  isSelected: (filename: string) => {
    return get().selectedFiles.has(filename)
  },

  setDeletionProgress: (progress: DeletionProgress | null) => {
    set({ deletionProgress: progress })
  },

  reset: () => {
    set({
      selectedFiles: new Set(),
      deletionProgress: null,
      lastSelectedIndex: null,
    })
  },
}))
